import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  Grid,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  OrganizationDashboardProvider,
  useOrganizationDashboard
} from './organizationDashboard/OrganizationDashboardContext';
import OrganizationDashboardControls from './organizationDashboard/OrganizationDashboardControls';
import OrganizationDashboardSection from './organizationDashboard/OrganizationDashboardSection';
import OrganizationCompaniesTable from './organizationDashboard/OrganizationCompaniesTable';
import OrganizationBudgetRollupCard from './organizationDashboard/OrganizationBudgetRollupCard';

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  website?: string;
  industry?: string;
  size?: string;
  isActive: boolean;
  defaultTasks?: string[];
  defaultCurrency?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface OrganizationFormData {
  name: string;
  slug: string;
  description: string;
  website: string;
  industry: string;
  size: string;
  isActive: boolean;
  defaultTasks: string[];
}

interface Company {
  id: string;
  name: string;
  organizationId: string;
  description?: string;
  isActive: boolean;
  companyType?: string;
}

interface UserOrganization {
  userId: string;
  organizationId: string;
  role: string;
  joinedAt: string;
  isActive: boolean;
  user: {
    id: string;
    email: string;
    displayName?: string;
    isActive: boolean;
    isVerified: boolean;
  };
}

interface OrganizationViewProps {
  organizationId: string;
  organizationName: string;
  onBack: () => void;
  onOpenCompany: (companyId: string, companyName: string) => void;
  userRole?: string;
}

export default function OrganizationView(props: OrganizationViewProps) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('EUR');

  useEffect(() => {
    setOrganization(null);
    setLoading(true);
    Promise.all([
      api.getUserOrganizations(),
      api.getOrganizationCurrency(props.organizationId).catch(() => null)
    ])
      .then(([orgRes, currencyRes]) => {
        if (orgRes.success && orgRes.data) {
          const userOrg = orgRes.data.find(
            (uo: { organization: { id: string } }) => uo.organization.id === props.organizationId
          );
          if (userOrg) {
            setOrganization(userOrg.organization);
          }
        }
        const code =
          currencyRes?.data?.defaultCurrency ||
          currencyRes?.data?.currency ||
          'EUR';
        if (typeof code === 'string' && code) {
          setCurrency(code);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [props.organizationId]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading organization...</Typography>
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Organization not found</Alert>
        <Button onClick={props.onBack} sx={{ mt: 2 }}>
          Back to Tree View
        </Button>
      </Box>
    );
  }

  return (
    <OrganizationDashboardProvider
      organizationId={props.organizationId}
      organizationCurrency={currency}
    >
      <OrganizationViewBody {...props} initialOrganization={organization} />
    </OrganizationDashboardProvider>
  );
}

function OrganizationViewBody({
  organizationId,
  onBack,
  onOpenCompany,
  userRole = 'MEMBER',
  initialOrganization
}: OrganizationViewProps & { initialOrganization: Organization }) {
  const navigate = useNavigate();
  const { accessGranted } = useOrganizationDashboard();
  const [error, setError] = useState<string | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(initialOrganization);
  const [members, setMembers] = useState<UserOrganization[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: initialOrganization.name,
    slug: initialOrganization.slug,
    description: initialOrganization.description || '',
    website: initialOrganization.website || '',
    industry: initialOrganization.industry || '',
    size: initialOrganization.size || '',
    isActive: initialOrganization.isActive,
    defaultTasks: initialOrganization.defaultTasks || []
  });
  const [newTaskInput, setNewTaskInput] = useState('');

  const canEdit = userRole === 'OWNER' || userRole === 'MANAGER';
  const economyVisible = accessGranted === true;

  const loadOrganization = async () => {
    try {
      setError(null);
      const response = await api.getUserOrganizations();
      if (response.success && response.data) {
        const userOrg = response.data.find(
          (uo: { organization: { id: string } }) => uo.organization.id === organizationId
        );
        if (userOrg) {
          const org = userOrg.organization;
          setOrganization(org);
          setFormData({
            name: org.name,
            slug: org.slug,
            description: org.description || '',
            website: org.website || '',
            industry: org.industry || '',
            size: org.size || '',
            isActive: org.isActive,
            defaultTasks: org.defaultTasks || []
          });
        } else {
          setError('Organization not found');
        }
      }
    } catch (err: any) {
      console.error('Error loading organization:', err);
      setError(err.response?.data?.message || 'Failed to load organization');
    }
  };

  const loadMembers = async () => {
    try {
      setMembersLoading(true);
      const response = await api.getOrganizationUsers(organizationId);
      const transformedMembers =
        response.data?.map((user: any) => ({
          userId: user.id,
          organizationId,
          role: user.organizationRole,
          joinedAt: user.joinedAt,
          isActive: true,
          user: {
            id: user.id,
            email: user.email,
            displayName: user.displayName,
            isActive: user.isActive || true,
            isVerified: user.isVerified || false
          }
        })) || [];
      setMembers(transformedMembers);
    } catch (err: any) {
      console.error('Error loading members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const response = await api.getCompanies({ organizationId, isActive: 'true' });
      setCompanies(response.data || []);
    } catch (err: any) {
      console.error('Error loading companies:', err);
    } finally {
      setCompaniesLoading(false);
    }
  };

  useEffect(() => {
    loadOrganization();
    loadMembers();
    loadCompanies();
  }, [organizationId]);

  const saveOrganizationUpdate = async (payload: OrganizationFormData) => {
    const response = await api.updateOrganization(organizationId, payload);
    if (!response.success) throw new Error(response.message || 'Failed to update organization');
    return response;
  };

  const handleSave = async () => {
    try {
      setError(null);
      await saveOrganizationUpdate(formData);
      await loadOrganization();
      setEditDialogOpen(false);
    } catch (err: any) {
      console.error('Error updating organization:', err);
      setError(err.response?.data?.message || 'Failed to update organization');
    }
  };

  const handleAddDefaultTask = async (taskName: string) => {
    if (!taskName || formData.defaultTasks.includes(taskName)) return;
    const newTasks = [...formData.defaultTasks, taskName];
    setFormData((prev) => ({ ...prev, defaultTasks: newTasks }));
    try {
      setError(null);
      await saveOrganizationUpdate({ ...formData, defaultTasks: newTasks });
      setOrganization((prev) => (prev ? { ...prev, defaultTasks: newTasks } : null));
    } catch (err: any) {
      console.error('Error saving default task:', err);
      setError(err.response?.data?.message || 'Failed to save default task');
      loadOrganization();
    }
  };

  const handleRemoveDefaultTask = async (taskName: string) => {
    const newTasks = formData.defaultTasks.filter((t) => t !== taskName);
    setFormData((prev) => ({ ...prev, defaultTasks: newTasks }));
    try {
      setError(null);
      await saveOrganizationUpdate({ ...formData, defaultTasks: newTasks });
      setOrganization((prev) => (prev ? { ...prev, defaultTasks: newTasks } : null));
    } catch (err: any) {
      console.error('Error saving default task:', err);
      setError(err.response?.data?.message || 'Failed to save default task');
      loadOrganization();
    }
  };

  if (!organization) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{organization.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditDialogOpen(true)}
            >
              Edit
            </Button>
          )}
          <Button variant="outlined" onClick={onBack}>
            Back to Tree View
          </Button>
        </Box>
      </Box>

      <OrganizationDashboardControls />
      <OrganizationDashboardSection />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <OrganizationCompaniesTable
                  companies={companies}
                  companiesLoading={companiesLoading}
                  canEdit={canEdit}
                  onOpenCompany={onOpenCompany}
                  onAddCompany={() => navigate(`/org/${organizationId}/manage/add-company`)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Members ({members.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {membersLoading ? (
                  <LinearProgress />
                ) : members.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Role</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {members.slice(0, 10).map((member) => (
                          <TableRow key={member.userId}>
                            <TableCell>{member.user.displayName || 'N/A'}</TableCell>
                            <TableCell>{member.user.email}</TableCell>
                            <TableCell>
                              <Chip label={member.role} size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No members found.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Organization Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">{organization.name}</Typography>
                  </Box>
                  {organization.description && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">{organization.description}</Typography>
                    </Box>
                  )}
                  {organization.website && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Website
                      </Typography>
                      <Typography variant="body1">
                        <a href={organization.website} target="_blank" rel="noopener noreferrer">
                          {organization.website}
                        </a>
                      </Typography>
                    </Box>
                  )}
                  {organization.industry && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Industry
                      </Typography>
                      <Typography variant="body1">{organization.industry}</Typography>
                    </Box>
                  )}
                  {organization.size && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Size
                      </Typography>
                      <Typography variant="body1">{organization.size}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={organization.isActive ? 'Active' : 'Inactive'}
                      color={organization.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {economyVisible && (
              <OrganizationBudgetRollupCard organizationId={organizationId} />
            )}
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Website"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              fullWidth
            />
            <TextField
              label="Industry"
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              fullWidth
            />
            <TextField
              label="Size"
              value={formData.size}
              onChange={(e) => setFormData({ ...formData, size: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />

            <Divider />
            <Typography variant="subtitle1">Default Tasks</Typography>
            <TextField
              label="Add Default Task"
              placeholder="Enter task name and press Enter"
              size="small"
              value={newTaskInput}
              onChange={(e) => setNewTaskInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newTaskInput.trim()) {
                  handleAddDefaultTask(newTaskInput.trim());
                  setNewTaskInput('');
                }
              }}
              fullWidth
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.defaultTasks.map((task) => (
                <Chip
                  key={task}
                  label={task}
                  onDelete={() => handleRemoveDefaultTask(task)}
                  size="small"
                />
              ))}
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
